// ---------------------------------------------------------------------------
// F3e C4: qual mensagem de histórico mostrar (uma por vez). Função pura; o App só passa o estado.
//
// Prioridade: erro > início dos dados > carregando.
//   erro        : falha de carga. A troca de série e o "tentar de novo" (ação do usuário que depende dos dados)
//                 avisam na hora; a pré-carga em segundo plano tenta de novo em silêncio (2 s, 4 s, 8 s...) e só
//                 avisa a partir da 3ª falha COM a vista a <= 800 velas da borda do lado que falhou.
//   início      : "início dos dados", por ~3 s, quando o usuário encosta na borda mais antiga com o fim real já
//                 confirmado pela fonte, ou arma a navegação com a vista a <= START_NEAR velas do início. Não aparece
//                 na abertura só porque a série inteira cabe na tela; reaparece a cada encostada.
//   carregando  : troca de série em andamento (com o progresso) ou a vista parada na borda esperando o bloco.
// Textos sem jargão; todo erro tem "tentar de novo".
// ---------------------------------------------------------------------------

import type { CandleErrorKind } from './candleSource';

/** falha em segundo plano: avisa a partir desta falha seguida... */
export const BG_FAIL_SHOW_ATTEMPT = 3;
/** ...e só com a vista a até tantas velas da borda do lado que falhou */
export const BG_FAIL_NEAR_EDGE = 800;
/** "início dos dados" fica na tela por */
export const START_PULSE_MS = 3000;
/** armar a navegação com a vista a até tantas velas do início mostra "início dos dados" */
export const START_NEAR = 100;

export interface FalhaMsg {
  tipo: CandleErrorKind;
  status: number | null;
  attempt: number;
  /** pré-carga ou carga de borda automática (true); troca de série ou "tentar de novo" (false) */
  background: boolean;
}

export interface EstadoMsg {
  falha: FalhaMsg | null;
  /** velas entre a vista e a borda carregada do lado da falha (Infinity = longe / não se aplica) */
  distBorda: number;
  /** troca de série em andamento: texto "ativo timeframe" e progresso "N/M" ('' = sem) */
  trocando: string | null;
  progresso: string;
  /** a vista parada na borda esperando o próximo bloco */
  esperandoBorda: boolean;
  /** até quando mostrar "início dos dados" (ms, mesmo relógio de `agora`) */
  inicioAte: number;
  agora: number;
  /** nome da fonte ("binance") */
  fonte: string;
}

export type TipoMsg = 'erro' | 'inicio' | 'carregando';
export interface Msg {
  tipo: TipoMsg;
  texto: string;
  /** mostra o botão "tentar de novo" */
  tentar: boolean;
  /** por que apareceu (diagnóstico) */
  motivo: string;
}

const ERRO: Record<CandleErrorKind, string> = {
  rede: 'Não foi possível carregar mais histórico agora.',
  outro: 'Não foi possível carregar mais histórico agora.',
  limite: 'Fonte de dados indisponível no momento.',
  bloqueio: 'Sem acesso ao histórico neste momento.',
  fonteTrocada: 'Os dados vieram de outra corretora; histórico pausado.',
};

/** A falha deve aparecer agora? */
export function falhaVisivel(f: FalhaMsg | null, distBorda: number): boolean {
  if (!f) return false;
  if (!f.background) return true;
  return f.attempt >= BG_FAIL_SHOW_ATTEMPT && distBorda <= BG_FAIL_NEAR_EDGE;
}

export function mensagemHistorico(s: EstadoMsg): Msg | null {
  if (s.falha && falhaVisivel(s.falha, s.distBorda)) {
    const f = s.falha;
    const texto = s.trocando && !f.background ? `Não foi possível abrir ${s.trocando} agora.` : ERRO[f.tipo];
    const motivo = `${f.background ? `pré-carga, ${f.attempt}ª falha, a ${Number.isFinite(s.distBorda) ? Math.round(s.distBorda) : '—'} velas da borda` : s.trocando ? 'troca de série' : 'tentar de novo'} (${f.tipo}${f.status ? ' ' + f.status : ''})`;
    return { tipo: 'erro', texto, tentar: true, motivo };
  }
  if (s.agora < s.inicioAte) return { tipo: 'inicio', texto: `Início dos dados — fonte ${s.fonte}`, tentar: false, motivo: 'encostou no início' };
  if (s.trocando) return { tipo: 'carregando', texto: `Carregando ${s.trocando}…${s.progresso ? ' ' + s.progresso : ''}`, tentar: false, motivo: 'troca de série' };
  if (s.esperandoBorda) return { tipo: 'carregando', texto: 'Carregando histórico…', tentar: false, motivo: 'vista na borda, bloco a caminho' };
  return null;
}
